import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { ExporterInsightQuery } from 'dashboard/models/exporter';
import {
  InsightQueriesPreviewResponse,
  InsightQueriesPreviewResults,
  InsightQuery,
} from 'dashboard/models/insight-queries';
import {
  archiveInsightQuery as archiveInsightQueryAction,
  setExpandedTablesAvailable,
  setInsightQueries,
  setInsightQueryResults,
  setNewInsightQuery,
  updateInsightQuery as updateInsightQueryAction,
} from 'dashboard/redux/slices/insight-queries';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel, ApiResource } from 'lib/interfaces/api';

export const createExporterInsightQuery =
  ({ query }: { query: string }) =>
  async () => {
    const params = {
      data: {
        query: query,
      },
    };

    await SessionClientDashboard.postResourceCreate<ExporterInsightQuery>({
      type: ApiModel.ExporterInsightQuery,
      attributes: {
        ...params,
      },
    });
  };

export const getInsightQueriesPreview =
  ({ query }: { query: string }): DashboardThunkAction =>
  async (dispatch: Dispatch) => {
    const { meta } = await SessionClientDashboard.postResourceSingletonActionNew<
      ApiResource<null>,
      { type: ApiModel.InsightQuery },
      InsightQueriesPreviewResponse
    >(ApiEndpoint.InsightQuery, 'preview', { type: ApiModel.InsightQuery }, { meta: { query } });

    dispatch(
      setInsightQueryResults({
        results: (meta?.results ?? []) as InsightQueriesPreviewResults,
        error: meta?.error ?? null,
      }),
    );
  };

export const createInsightQuery =
  ({ name, query }: { name: string; query: string }): DashboardThunkAction =>
  async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceCreate<InsightQuery>({
      type: ApiModel.InsightQuery,
      attributes: {
        name,
        query,
      },
    });
    dispatch(setNewInsightQuery({ query: response.data }));
  };

export const getInsightQueries = (): DashboardThunkAction => async (dispatch: Dispatch) => {
  const response = await SessionClientDashboard.listResource<InsightQuery>(
    ApiEndpoint.InsightQuery,
  );
  dispatch(setInsightQueries({ queries: response.data }));
};

export const updateInsightQuery =
  ({ id, name, query }: { id: string; name: string; query: string }): DashboardThunkAction =>
  async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.updateResource<InsightQuery>({
      type: ApiModel.InsightQuery,
      id,
      attributes: {
        name,
        query,
      },
    });
    dispatch(updateInsightQueryAction({ query: response.data }));
  };

export const archiveInsightQuery =
  ({ id }: { id: string }): DashboardThunkAction =>
  async (dispatch: Dispatch) => {
    await SessionClientDashboard.postResourceAction<InsightQuery>(
      {
        type: ApiModel.InsightQuery,
        id,
      },
      'archive',
    );
    dispatch(archiveInsightQueryAction({ id }));
  };

export const expandData = (): DashboardThunkAction => async (_: Dispatch) => {
  await SessionClientDashboard.postResourceSingletonActionNew<
    ApiResource<null>,
    { type: ApiModel.InsightQuery }
  >(ApiEndpoint.InsightQuery, 'expand-data', { type: ApiModel.InsightQuery });
};

export const checkExpandedTableExists = (): DashboardThunkAction => async (dispatch: Dispatch) => {
  const { meta } = await SessionClientDashboard.postResourceSingletonActionNew<
    ApiResource<null>,
    { type: ApiModel.InsightQuery },
    { expanded_table_exists: boolean }
  >(ApiEndpoint.InsightQuery, 'check-table-status', { type: ApiModel.InsightQuery });
  dispatch(
    setExpandedTablesAvailable({ expandedTableExists: meta?.expanded_table_exists ?? false }),
  );
};
